import T from 'T'

import type { PlacementCandidate } from 'components/inventory/containers/utils'

// Mirrors crm-common ContainerStatus; only the value a settled retrieval writes.
export const CONTAINER_STATUS = { AVAILABLE: 'AVAILABLE' } as const

// What became of a container left behind. Only scheduled/retrieved/left are offered today.
export type RetrievalKind = 'scheduled' | 'retrieved' | 'left' | 'customer-owned' | 'deleted'

// Left on site is an accounting outcome, so it says which of the two things happened.
export type LeftOnSiteOutcome = 'sold' | 'unassign'

export interface RetrievalResolution {
  kind: RetrievalKind
  date?: Date | null
  yardId?: string
  leftOutcome?: LeftOnSiteOutcome
  // The pickup WO that IS this removal — when set, the WO completion records the move.
  workOrderId?: string
  // Service action for creating the pickup WO. Only meaningful for `scheduled`.
  actionId?: string
}

export interface SettleableRow extends PlacementCandidate {
  id: string
}

export const RETRIEVAL_KINDS: RetrievalKind[] = ['scheduled', 'retrieved', 'left']

export const RETRIEVAL_LABEL: Record<RetrievalKind, string> = {
  scheduled: T.RETRIEVAL_SCHEDULED,
  retrieved: T.RETRIEVED,
  left: T.LEFT_ON_SITE,
  'customer-owned': T.CUSTOMER_OWNED,
  deleted: T.DELETE_THE_RECORD,
}

// The can stays where it is and stops being the hauler's, so there is nothing to retrieve.
export const isOwnershipCorrection = (kind: RetrievalKind) => kind === 'customer-owned'

// Nothing was ever there to bring back — the row the system created when the service was set up.
export const isRecordOnly = (kind: RetrievalKind) => kind === 'deleted'

export const LEFT_ON_SITE_OUTCOMES: LeftOnSiteOutcome[] = ['unassign', 'sold']

export const LEFT_ON_SITE_LABEL: Record<LeftOnSiteOutcome, string> = {
  unassign: T.UNASSIGN,
  sold: T.GIVEN_OR_SOLD_TO_CUSTOMER,
}

// One list, not a kind that opens a second picker (Mark, 2026-09-17); the stored shape is unchanged.
export type SettlementOutcome = 'scheduled' | 'retrieved' | LeftOnSiteOutcome

export const SETTLEMENT_OUTCOMES: SettlementOutcome[] = ['scheduled', 'retrieved', 'unassign', 'sold']

export const SETTLEMENT_OUTCOME_LABEL: Record<SettlementOutcome, string> = {
  scheduled: T.RETRIEVAL_SCHEDULED,
  retrieved: T.RETRIEVED,
  unassign: T.UNASSIGN,
  sold: T.GIVEN_OR_SOLD_TO_CUSTOMER,
}

// '' covers both unanswered and the planner-only kinds no picker offers.
export const outcomeOf = (resolution?: RetrievalResolution): SettlementOutcome | '' => {
  if (!resolution) return ''
  if (resolution.kind === 'scheduled' || resolution.kind === 'retrieved') return resolution.kind
  return resolution.kind === 'left' ? resolution.leftOutcome ?? '' : ''
}

// `normalize` clears a stale `leftOutcome` on the way back out.
export const resolutionFor = (outcome: SettlementOutcome): Pick<RetrievalResolution, 'kind' | 'leftOutcome'> =>
  outcome === 'sold' || outcome === 'unassign' ? { kind: 'left', leftOutcome: outcome } : { kind: outcome }

export const settlementLabel = (resolution: RetrievalResolution): string => {
  const outcome = outcomeOf(resolution)
  return outcome ? SETTLEMENT_OUTCOME_LABEL[outcome] : RETRIEVAL_LABEL[resolution.kind]
}

// A container that comes back needs somewhere to come back to; one that never moves does not.
export const retrievalNeedsYard = (kind: RetrievalKind) => kind === 'scheduled' || kind === 'retrieved'

export const retrievalNeedsLeftOutcome = (kind: RetrievalKind) => kind === 'left'

// Answered completely, not merely picked: `left` without its aftermath is the row people click past.
export const isSettled = (resolution?: RetrievalResolution): boolean => {
  if (!resolution) return false
  const { kind, date, yardId, leftOutcome } = resolution

  if (kind === 'scheduled' && !date) return false
  if (retrievalNeedsYard(kind) && !yardId) return false

  return !retrievalNeedsLeftOutcome(kind) || Boolean(leftOutcome)
}
